import * as Sentry from "@sentry/react";
import {
  API_RESPONSE_EVENT,
  API_SAVE_AUDIO_EVENT,
  API_SAVE_CHAT_EVENT,
  API_SAVE_MCQ_EVENT,
  NEW_DATA_EVENT,
  PAUSE_TIMER_EVENT,
  QUESTION_PREVIEW_EVENT,
  RESUME_TIMER_EVENT
} from "constant";
import { storage as configStorage } from "./storage";

const TYPE = "QUIZ";
const GAME_MOUNT = "GAME_MOUNT";
const GAME_DATA_SET = "GAME_DATA_SET";
const GAME_DATA_SAVE = "GAME_DATA_SAVE";
const GAME_END = "GAME_END";
const GAME_UNMOUNT = "GAME_UNMOUNT";
const GAME_CREATE_MASTER_SESSION = "GAME_CREATE_MASTER_SESSION";
const SEND_GAME_RESPONSE = "SEND_GAME_RESPONSE";
const SEND_GAME_AUDIO_SAVE_RESPONSE = "SEND_GAME_AUDIO_SAVE_RESPONSE";
const SEND_MCQ_SAVE_RESPONSE = "SEND_MCQ_SAVE_RESPONSE";
const GAME_QUESTION_PREVIEW = "GAME_QUESTION_PREVIEW";
const GAME_BLURRED = "GAME_BLURRED";
const SEND_CHAT_SAVE_RESPONSE = 'SEND_CHAT_SAVE_RESPONSE';

export const SET_CONFIG_LANGUAGE = "SET_CONFIG_LANGUAGE";

/**
 * Get the parent app origin
 * @returns {string}
 */
const getParentOrigin = () => {
  const parentOrigin =
    window.location !== window.parent.location
      ? document.referrer
      : document.location.href;
  return parentOrigin.endsWith("/") ? parentOrigin.slice(0, -1) : parentOrigin;
};

/**
 * Event handler for catching postMessage events from parent app
 * @param {*} event
 * @returns
 */
const messageEventHandler = (event) => {
  // Ignore other message events which are not from assessment center origin
  if (event.origin !== getParentOrigin()) {
    return;
  }

  console.log("🚀 ~ Quiz Event Handler ::", event);
  // Saving authToken & gameplayId from Assessment centre
  const { gameId, gameLevelData, messages, gameplayId, configLanguage,
    mediaId, status, user = {}, organization = "", parent = "", questionId = "" } = event.data;

  if (configLanguage) {
    window.dispatchEvent(new CustomEvent(SET_CONFIG_LANGUAGE, { detail: configLanguage }));
  }

  // status incoming -> from CC
  if (status === GAME_QUESTION_PREVIEW) {
    configStorage.set.quizData(gameLevelData);
    window.dispatchEvent(new Event(QUESTION_PREVIEW_EVENT));
    return;
  }

  // chat module - storing api response and invoking event
  if (status === SEND_GAME_RESPONSE) {
    configStorage.set.chats(messages);
    window.dispatchEvent(new Event(API_RESPONSE_EVENT));
    return;
  }

  // Only for chat submit answer
  if (status === SEND_CHAT_SAVE_RESPONSE) {
    window.dispatchEvent(new CustomEvent(API_SAVE_CHAT_EVENT, {
      detail: {
        question: {
          id: questionId,
        },
      },
    }))
    return
  }

  // Only for MCQs -> Check for save and dispatch window event for nextQuestion
  if (status === SEND_MCQ_SAVE_RESPONSE) {
    window.dispatchEvent(new Event(API_SAVE_MCQ_EVENT));
    return;
  }

  // check for save audio response in post msg
  if (status === SEND_GAME_AUDIO_SAVE_RESPONSE) {
    window.dispatchEvent(new CustomEvent(API_SAVE_AUDIO_EVENT, { detail: { mediaId } }));
    return;
  }

  /**
    trigger the event during the game.
    return statement to avoid setting LS with quiz data 
   *   */
  if (status === PAUSE_TIMER_EVENT || status === RESUME_TIMER_EVENT) {
    window.dispatchEvent(new Event(status))
    return
  }

  /**
   trigger the event when game mounts.
   No return statement so that game data can be set and loaded.
   */
  if (status === `${PAUSE_TIMER_EVENT}-MOUNT`) {
    window.dispatchEvent(new Event(PAUSE_TIMER_EVENT))
  }


  // Setting this way since some extensions override iframe and send their data keys in message
  // So only when data keys relevant to code arrive, populate data. 
  gameId && configStorage.set.gameId(gameId);
  gameLevelData && configStorage.set.quizData(gameLevelData);
  user && configStorage.set.user(user);
  organization && configStorage.set.org(organization);
  parent && configStorage.set.parentApp(parent);
  gameplayId && configStorage.set.gameplayId(gameplayId);

  // Setting user context for sentry
  try {
    Sentry.setUser(user);
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);
  }

  // sending start game event
  sendGameDataSetMessage();

  // creating storage event
  window.dispatchEvent(new Event(NEW_DATA_EVENT));
};

/**
 * First message triggers event and is sent to Sentry logs.
 * 
 * Second message also triggers a message but is not sent to Sentry logs.
 * This acts as callback to flush event queue in sentry.
 *  
 * Bug in sentry which does not empty queue without calling flush() or close() -> Both of which clear the entire context in Sentry.
 * 
 * Alternatively, send two messages -> like done here
 * And filter out second message in Sentry.init -> beforeSend method
 * 
 *  */
const captureSentryEvent = async ({ tag, eventMessage, payload, additionalTags = {} }) => {
  const linkId = configStorage.get.gameplayId();
  Sentry.captureMessage(eventMessage, {
    tags: {
      status: tag,
      linkId,
      ...additionalTags
    },
    level: 'log',
    extra: { payload, linkId }
  });
  Sentry.captureMessage('Sentry log initiated.');
};

/**
 * Send message to the parent app (Assessment centre)
 * @param {any} message message to be sent
 * @param {object} options optional params
 * @return
 */
const sendMessageToGamecenter = (message, options) => {
  const { addGameName } = options ?? {};

  const url = getParentOrigin();
  const data = addGameName
    ? { ...message, game: process.env.REACT_APP_GAME_NAME, type: TYPE }
    : { ...message, type: TYPE };
  window.parent.postMessage(data, url);
};

/**
 * Event for game mount
 */
const sendGameLoadMessage = () => {
  const message = { status: GAME_MOUNT };
  sendMessageToGamecenter(message);
};

/**
 * Event to be sent when all game data has been set
 * and game can be played
 */
const sendGameDataSetMessage = () => {
  const message = { status: GAME_DATA_SET };
  sendMessageToGamecenter(message);
};

/**
 * Event for gameLevelData save, only data
 * is sent for this game.
 */
const sendGameDataSaveMessage = (gameLevelData) => {
  const message = { status: GAME_DATA_SAVE, gameLevelData };
  const additionalTags = {
    quizId: gameLevelData?.quizId,
    questionId: gameLevelData?.questionId
  };
  captureSentryEvent({
    tag: "game-data-save",
    eventMessage: "Gamedata Save: Capture payload",
    payload: message,
    additionalTags
  });

  sendMessageToGamecenter(message);
};

/**
 * Event for game end, send gameId to parent app
 */
const sendGameEndMessage = (timeSpent) => {
  const message = {
    status: GAME_END,
    gameId: configStorage.get.gameId(),
    timeSpent,
  };

  // Add Sentry logging for game end
  captureSentryEvent({
    tag: "game-end",
    eventMessage: "Game End",
    payload: message
  });

  sendMessageToGamecenter(message);
};

const sendGameUnmountMessage = () => {
  const message = {
    status: GAME_UNMOUNT,
    gameId: configStorage.get.gameId(),
  }
  sendMessageToGamecenter(message)
}

const createGameMasterSession = () => {
  const message = {
    status: GAME_CREATE_MASTER_SESSION
  };
  sendMessageToGamecenter(message);
};

const sendGameBlurredMessage = () => {
  const message = { status: GAME_BLURRED };
  sendMessageToGamecenter(message);
};

export const GC = {
  messageEventHandler,
  sendGameLoadMessage,
  sendGameDataSaveMessage,
  sendGameEndMessage,
  sendGameUnmountMessage,
  createGameMasterSession,
  sendGameBlurredMessage
};
